import {
	breakItUp,
	getDataFromGrid,
	getColumnsFromGrid,
	getSortIndexesFromGrid,
} from '../../../utils';
import equal from 'fast-deep-equal';
import type {AttributeValues, GridEditor, Permissions} from '../../../types';

import {useSelectionStore} from '../../selection';

import all from './all';

const getValues = (
	gridEditor: GridEditor,
	selectionStore: ReturnType<typeof useSelectionStore>,
	permissions: Permissions,
	setProgress: (progress: number) => void,
	ignoreParseOut?: boolean,
) => {
	let aborted = false;
	let completed = false;

	const worker = async () => {
		if (selectionStore.state.all)
			return all(gridEditor, permissions, setProgress);

		const {round} = Math;

		const data = getDataFromGrid(gridEditor);
		const columns = getColumnsFromGrid(gridEditor);
		const sortIndex = getSortIndexesFromGrid(gridEditor);
		const {numColumns} = gridEditor;

		const selectedColumnObjects = columns.filter((column) =>
			selectionStore.state.columns.includes(column.name),
		);
		const selectedColumnCount = selectedColumnObjects.length;

		const attrValues: AttributeValues = {};
		const valuesWritables: Record<string, boolean[]> = {};

		const selectedRows = selectionStore.state.rows;
		const selectedRowCount = selectionStore.state.rowCount;

		const rowIndexesUpdating: number[] = selectedRows;
		const columnIndexesUpdating: number[] = selectedColumnObjects.map(
			(column) => column._index,
		);

		let processCount = 3;
		if (!selectedRowCount) processCount--;
		if (!selectedColumnCount) processCount--;
		if (!selectionStore.state.loose) processCount--;

		if (selectedRowCount) {
			// First rows
			const rowAmount = selectedRowCount <= 100 ? 25 : 100;
			await breakItUp(
				{
					amount: rowAmount,
					size: selectedRowCount,
				},
				(index) => {
					if (aborted) return false;

					setProgress(
						round(((index + 1) / selectedRowCount / processCount) * 100),
					);

					const rowIndex = selectedRows[index];
					const realIndex = sortIndex[rowIndex];
					const rowData = data[realIndex];
					if (!rowData) return;

					const perms = permissions[rowData.assetId];

					for (let x = 0, y = numColumns; x < y; x++) {
						const column = columns[x];
						if (!column) continue;

						const {name, properties} = column;
						const existing = attrValues[name];

						if (!valuesWritables[name]) valuesWritables[name] = [];
						valuesWritables[name].push(
							!properties.readOnly && (!perms || !perms.includes(name)),
						);

						if (
							// properties.readOnly ||
							existing?.multipleValues // ||
							// (perms && perms.includes(name))
						)
							continue;

						const value = ignoreParseOut
							? rowData[name]
							: properties.parseOutValue
							? properties.parseOutValue(rowData[name])
							: rowData[name];

						if (existing === void 0) {
							attrValues[name] = {value};
							continue;
						}

						if (!equal(value, existing.value)) {
							attrValues[name] = {
								multipleValues: true,
								value: undefined,
							};
						}
					}
				},
			);
		}

		if (aborted) return;

		// Then get columns
		if (selectedColumnCount) {
			const dataSize = data.length;
			const columnAmount = dataSize <= 100 ? 25 : 100;
			const multiValueFields: string[] = [];
			await breakItUp(
				{
					amount: columnAmount,
					size: data.length,
				},
				(index) => {
					if (aborted) return false;

					setProgress(round(((index + 1) / dataSize / processCount) * 100));

					if (rowIndexesUpdating.includes(index)) return;

					const realIndex = sortIndex[index];
					const rowData = data[realIndex];
					if (!rowData) return;

					const perms = permissions[rowData.assetId];

					if (multiValueFields.length === selectedColumnCount) return false;

					for (let x = 0, total = selectedColumnCount; x < total; x++) {
						const columnObject = selectedColumnObjects[x];
						if (!columnObject) continue;

						const {name, properties} = columnObject;
						const existing = attrValues[name];

						if (!valuesWritables[name]) valuesWritables[name] = [];
						valuesWritables[name].push(
							!properties.readOnly && (!perms || !perms.includes(name)),
						);

						if (
							existing?.multipleValues // ||
							//properties.readOnly ||
							// (perms && perms.includes(name))
						)
							continue;

						const value = ignoreParseOut
							? rowData[name]
							: properties.parseOutValue
							? properties.parseOutValue(rowData[columnObject.name])
							: rowData[name];

						if (existing === void 0) {
							attrValues[name] = {value};
							continue;
						}

						if (equal(value, existing.value)) continue;

						multiValueFields.push(name);
						attrValues[name] = {
							multipleValues: true,
							value: undefined,
						};
					}
				},
			);
		}

		if (aborted) return;

		// Then points
		if (selectionStore.state.loose) {
			const points = await selectionStore.getPoints();
			const pointsSize = points.length;
			const amount = pointsSize <= 100 ? 25 : 100;
			await breakItUp(
				{
					amount,
					size: pointsSize,
					increment: 2,
				},
				(index) => {
					if (aborted) return false;

					setProgress(round(((index + 1) / pointsSize / processCount) * 100));

					const x = points[index];
					const y = points[index + 1];

					const realIndex = sortIndex[y];
					const rowData = data[realIndex];
					const column = columns[x];
					if (!rowData || !column) return;

					const {name, properties} = column;
					const existing = attrValues[name];
					const perms = permissions[rowData.assetId];

					if (
						columnIndexesUpdating.includes(x) ||
						rowIndexesUpdating.includes(y)
					)
						return;

					if (!valuesWritables[name]) valuesWritables[name] = [];
					valuesWritables[name].push(
						!properties.readOnly && (!perms || !perms.includes(name)),
					);

					if (
						// columnIndexesUpdating.includes(x) ||
						// rowIndexesUpdating.includes(y) ||
						existing?.multipleValues // ||
						// properties.readOnly ||
						// (perms && perms.includes(name))
					)
						return;

					const value = ignoreParseOut
						? rowData[name]
						: properties.parseOutValue
						? properties.parseOutValue(rowData[name])
						: rowData[name];

					if (existing === void 0) {
						attrValues[name] = {value};
						return;
					}

					if (equal(value, existing.value)) return;

					attrValues[name] = {
						multipleValues: true,
						value: undefined,
					};
				},
			);
		}

		if (aborted) return;

		Object.entries(valuesWritables).forEach(([name, writable]) => {
			attrValues[name].readOnly = !writable.some((w) => w === true);
		});

		setProgress(100);
		completed = true;

		return attrValues;
	};

	return {
		abort() {
			if (aborted) return;
			aborted = true;
			!completed && setProgress(100);
		},
		job: worker(),
	};
};

export default getValues;
